import * as adminUserApi from '@apis/admin-user';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ROLE_LABELS } from '@consts/role';
import { AdminUser } from '@models/auth';
import { ChildInfo } from '@models/chat';
import { useQuery } from '@tanstack/react-query';
import { Card, Descriptions, Popover, Spin, Tag } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';

interface UserInfoTabProps {
  userId?: string;
  user?: AdminUser;
  loading?: boolean;
}

const formatDate = (date?: Date | string | null) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

const formatBool = (value?: boolean) => (value ? '⭕' : 'X');

const formatChildGender = (gender?: string): string => {
  if (!gender) return '-';
  const normalized = gender.toLowerCase();
  if (normalized === 'm' || normalized === 'male') return '남아';
  if (normalized === 'f' || normalized === 'female') return '여아';
  return gender;
};

const formatChildBirthday = (birthday?: string): string => {
  if (!birthday) return '-';
  const parsed = moment(birthday, ['YYYY-MM-DD', 'YYYYMMDD'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : birthday;
};

const RECENT_ACTIVE_AT_DESCRIPTION = '최근 활동일 기준: 로그인/로그아웃/서비스접속/이탈 중 마지막에 발생한 시간';

const renderIntelligenceAssessmentStatus = (
  child: ChildInfo,
  assessedChildNameSet: Set<string>,
  assessmentLoading: boolean,
): React.ReactNode => {
  if (assessmentLoading) return <Spin size="small" />;
  const childName = child.name?.trim();
  if (!childName) return '-';
  return assessedChildNameSet.has(childName) ? '완료' : '미완료';
};

export const UserInfoTab: React.FC<UserInfoTabProps> = ({ userId, user, loading }) => {
  const assessmentChildNamesQuery = useQuery({
    queryKey: [`user-detail-assessment-child-names-${userId}`],
    queryFn: () => adminUserApi.getUserAssessmentChildNames(userId!),
    enabled: !!userId,
  });

  const assessedChildNameSet = useMemo(() => {
    const names = assessmentChildNamesQuery.data?.data?.childNames || [];
    return new Set(names.map((name) => name.trim()));
  }, [assessmentChildNamesQuery.data?.data?.childNames]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!user) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>사용자 정보를 불러올 수 없습니다.</div>;
  }

  const profile = user.profile;
  const address = profile?.address;
  const loginMethods = user.loginMethods;

  const loginChannels: { name: string; email: string }[] = [];
  if (loginMethods?.kakao) loginChannels.push({ name: '카카오', email: loginMethods.kakao.email || '-' });
  if (loginMethods?.naver) loginChannels.push({ name: '네이버', email: loginMethods.naver.email || '-' });
  if (loginMethods?.apple) loginChannels.push({ name: '애플', email: loginMethods.apple.email || '-' });
  if (loginMethods?.email?.address) {
    loginChannels.push({
      name: loginMethods.email.hasPassword ? '이메일' : '이메일(미설정)',
      email: loginMethods.email.address,
    });
  }
  if (loginChannels.length === 0 && user.emails?.length) {
    loginChannels.push({ name: '이메일', email: user.emails[0]?.address || '-' });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Card title="기본 프로필" size="small">
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="이름">{profile?.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="닉네임">{profile?.nickname || '-'}</Descriptions.Item>
          <Descriptions.Item label="전화번호">{profile?.phone || '-'}</Descriptions.Item>
          <Descriptions.Item label="역할">
            {(user.roles || []).map((role) => (
              <Tag key={role}>{ROLE_LABELS[role] || role}</Tag>
            ))}
          </Descriptions.Item>
          <Descriptions.Item label="가입 채널">
            {loginChannels.length === 0 ? (
              '-'
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {loginChannels.map((c, idx) => (
                  <div key={idx}>
                    {c.name} / {c.email}
                  </div>
                ))}
              </div>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="이메일 설정 대기">
            {user.pendingEmailLogin
              ? `${user.pendingEmailLogin.email} (만료 ${formatDate(user.pendingEmailLogin.expiresAt)})`
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="자기소개">{profile?.message || '-'}</Descriptions.Item>
          <Descriptions.Item label="가입일">{formatDate(user.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="탈퇴일">
            {user.deletedAt ? <span style={{ color: 'red' }}>{formatDate(user.deletedAt)}</span> : '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                최근 활동일
                <Popover content={<div style={{ maxWidth: 260 }}>{RECENT_ACTIVE_AT_DESCRIPTION}</div>}>
                  <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'pointer' }} />
                </Popover>
              </span>
            }>
            {formatDate(profile?.activeAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="수신 동의" size="small">
        <Descriptions column={3} size="small" bordered>
          <Descriptions.Item label="이메일 마케팅">{formatBool(profile?.marketingEmailAgree)}</Descriptions.Item>
          <Descriptions.Item label="SMS 마케팅">{formatBool(profile?.marketingSMSAgree)}</Descriptions.Item>
          <Descriptions.Item label="기타 알림">{formatBool(profile?.otherAlarmAgree)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="주소" size="small">
        {address ? (
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="시·도">{address.sido || '-'}</Descriptions.Item>
            <Descriptions.Item label="시·군·구">{address.sigungu || '-'}</Descriptions.Item>
            <Descriptions.Item label="주소" span={2}>
              {[address.address, address.address2].filter(Boolean).join(' ') || '-'}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <span style={{ color: '#888' }}>등록된 주소가 없습니다.</span>
        )}
      </Card>

      <Card title="메모" size="small">
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {(user as any).memo || <span style={{ color: '#888' }}>없음</span>}
        </div>
      </Card>

      <Card title="자녀 정보" size="small">
        {profile?.children && profile.children.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {profile.children.map((child, idx) => (
              <div key={idx}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>{child.name || `자녀 ${idx + 1}`}</div>
                <Descriptions column={2} size="small" bordered>
                  <Descriptions.Item label="생년월일">{formatChildBirthday(child.birthday)}</Descriptions.Item>
                  <Descriptions.Item label="성별">{formatChildGender(child.gender)}</Descriptions.Item>
                  <Descriptions.Item label="MBTI">{child.mbtiType || '-'}</Descriptions.Item>
                  <Descriptions.Item label="지능검사 여부">
                    {renderIntelligenceAssessmentStatus(
                      child,
                      assessedChildNameSet,
                      assessmentChildNamesQuery.isLoading,
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            ))}
          </div>
        ) : (
          <span style={{ color: '#888' }}>등록된 자녀 정보가 없습니다.</span>
        )}
      </Card>

      <Card title="관심분야 / 멤버십 / 리워드" size="small">
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="관심분야">
            {profile?.categorySubList && profile.categorySubList.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {profile.categorySubList.map((cat) => (
                  <Tag key={cat}>{cat}</Tag>
                ))}
              </div>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="멤버십">
            {user.membership?.name ? <Tag color={user.membership.color || 'default'}>{user.membership.name}</Tag> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="리워드 코드">{(user as any).rewardCode || '-'}</Descriptions.Item>
          <Descriptions.Item label="가입 시 입력한 코드">
            {(user as any).referral?.rewardCode || (user as any).referral?.eventCode || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {user.partnerProfile && (
        <Card title="파트너 프로필 (요약)" size="small">
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="소개">{user.partnerProfile.intro || '-'}</Descriptions.Item>
            <Descriptions.Item label="학력">
              {user.partnerProfile.academics?.length ? `${user.partnerProfile.academics.length}건` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="경력">
              {user.partnerProfile.careers?.length ? `${user.partnerProfile.careers.length}건` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="자격">
              {user.partnerProfile.certificates?.length ? `${user.partnerProfile.certificates.length}건` : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </div>
  );
};
